import { useMemo } from "react";

/**
 * Persiste l'ordre des biens d'une liste (après filtres + tri) dans
 * sessionStorage, pour permettre la navigation prev/next depuis la page
 * détail via les flèches du fil d'Ariane.
 *
 * Pattern :
 *   - Liste : appelle `setListOrder("biens", sortedIds)` à chaque rendu
 *     (typiquement dans un useEffect avec [sorted]).
 *   - Détail : appelle `useListNavigation("biens", currentId)` pour
 *     obtenir { prevId, nextId, position, total }.
 *
 * Clés conventionnelles :
 *   - "biens" → BiensListPage
 *   - "apparts-for-rent" → AppartsForRentPage
 *   - "suivi-els" → SuiviElsPage
 *   - "suivi-publication" → SuiviPublicationPage
 *   - "planning-photos" → PlanningPhotosPage
 *   - "etat-des-lieux" → EtatDesLieuxPage
 */

const STORAGE_PREFIX = "list-order:";

export function setListOrder(key: string, ids: string[]): void {
  try {
    sessionStorage.setItem(STORAGE_PREFIX + key, JSON.stringify(ids));
  } catch {
    // sessionStorage full ou indisponible — non bloquant.
  }
}

export function getListOrder(key: string): string[] {
  try {
    const raw = sessionStorage.getItem(STORAGE_PREFIX + key);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed)
      ? parsed.filter((x): x is string => typeof x === "string")
      : [];
  } catch {
    return [];
  }
}

export type ListNavigation = {
  prevId: string | null;
  nextId: string | null;
  position: number;
  total: number;
};

export function useListNavigation(key: string, currentId: string | undefined): ListNavigation {
  return useMemo(() => {
    if (!currentId) return { prevId: null, nextId: null, position: 0, total: 0 };
    const ids = getListOrder(key);
    const idx = ids.indexOf(currentId);
    if (idx === -1) return { prevId: null, nextId: null, position: 0, total: ids.length };
    return {
      prevId: idx > 0 ? ids[idx - 1] : null,
      nextId: idx < ids.length - 1 ? ids[idx + 1] : null,
      position: idx + 1,
      total: ids.length,
    };
    // currentId only — `key` est stable et ids viennent du sessionStorage,
    // recalculer à chaque mount/navigation suffit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentId]);
}
